import React from 'react';
import { Link } from 'react-router-dom';
import { QrCodeIcon, MessageCircle, Mail, Phone, Clock, HelpCircle } from 'lucide-react';

export default function Support() {
  const supportOptions = [
    {
      icon: <HelpCircle className="h-12 w-12 text-blue-600" />,
      title: "Help Center",
      description: "Browse our comprehensive FAQ and step-by-step guides",
      action: "Browse Help Center",
      link: "/help"
    },
    {
      icon: <MessageCircle className="h-12 w-12 text-green-600" />,
      title: "Live Chat",
      description: "Chat with our support team in real-time",
      action: "Start Chat",
      link: "/contact"
    },
    {
      icon: <Mail className="h-12 w-12 text-purple-600" />,
      title: "Email Support",
      description: "Send us a detailed message and we'll respond within 24 hours",
      action: "Send Email",
      link: "mailto:support@icomment.app"
    }
  ];

  const faqs = [
    {
      question: "How do I create my first QR code?",
      answer: "Sign up for an account, go to your dashboard, and click 'Generate QR Code'. Add a title and optional greeting message, then download your QR code."
    },
    {
      question: "What happens when someone scans my QR code?",
      answer: "The first person to scan becomes the 'Owner'. After that, anyone who scans can send anonymous messages to the owner."
    },
    {
      question: "How do I receive notifications?",
      answer: "You'll receive email notifications when someone sends you a message. Premium users also get SMS notifications."
    },
    {
      question: "Can I reply to messages?",
      answer: "Yes! You can reply to messages through the app. The conversation stays anonymous but allows two-way communication."
    },
    {
      question: "Is my personal information safe?",
      answer: "Absolutely. We never share your contact information with message senders. All communication is routed through our secure platform."
    },
    {
      question: "Can I customize my QR codes?",
      answer: "Basic and Premium users can customize QR code designs, colors, and add logos to match their brand or style."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <QrCodeIcon className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">iComment.app</span>
            </Link>
            <nav className="flex items-center space-x-6">
              <Link to="/" className="text-gray-600 hover:text-gray-900">Home</Link>
              <Link to="/auth" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">Sign In</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            We're Here to
            <span className="block text-yellow-300">Help You</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            Get the support you need to make the most of iComment.app. We're committed to your success.
          </p>
        </div>
      </section>

      {/* Support Options */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">How Can We Help?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {supportOptions.map((option, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow text-center">
                <div className="mb-6 flex justify-center">{option.icon}</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{option.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{option.description}</p>
                <Link 
                  to={option.link}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors inline-block"
                >
                  {option.action}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Hours */}
      <section className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Clock className="h-12 w-12 text-blue-600 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Support Hours</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Live Chat & Phone</h3>
              <p className="text-gray-600">Monday - Friday: 9:00 AM - 6:00 PM EST</p>
              <p className="text-gray-600">Saturday: 10:00 AM - 4:00 PM EST</p>
              <p className="text-gray-600">Sunday: Closed</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Email Support</h3>
              <p className="text-gray-600">24/7 - We respond within 24 hours</p>
              <p className="text-gray-600">Premium users: Response within 4 hours</p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick FAQ */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Quick Answers</h2>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link 
              to="/help"
              className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              View All Help Articles
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}